import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Github, 
  Linkedin, 
  Mail, 
  Home as HomeIcon, 
  User as UserIcon, 
  Briefcase as ProjectsIcon, 
  Sparkles as SkillsIcon, 
  Send as SendIcon 
} from 'lucide-react';

interface MobileNavProps {
  scrollToSection: (sectionId: string) => void;
  activeSection?: string;
}

export const MobileNav: React.FC<MobileNavProps> = ({ scrollToSection, activeSection = 'hero' }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleNavClick = (sectionId: string) => {
    scrollToSection(sectionId);
    setIsOpen(false);
  };

  // Prevent background scrolling when menu drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const navItems = [
    { label: 'Home', id: 'hero', icon: HomeIcon },
    { label: 'About', id: 'about', icon: UserIcon },
    { label: 'Projects', id: 'projects', icon: ProjectsIcon },
    { label: 'Skills', id: 'skills', icon: SkillsIcon },
    { label: 'Contact', id: 'contact', icon: SendIcon },
  ];

  const menuVariants = {
    closed: {
      opacity: 0,
      x: '100%',
      transition: { duration: 0.25, ease: 'easeInOut' }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, x: 30 },
    open: { opacity: 1, x: 0 }
  };

  return (
    <>
      {/* Mobile menu trigger button */}
      <motion.button
        whileTap={{ scale: 0.92 }}
        onClick={toggleMenu}
        className="lg:hidden p-2 xs:p-2.5 rounded-2xl bg-brand-purple/10 dark:bg-brand-purple/25 text-brand-navy dark:text-brand-dark-text border border-brand-purple/30 dark:border-brand-purple/40 hover:bg-brand-purple/20 dark:hover:bg-brand-purple/35 transition-all shadow-sm flex items-center justify-center cursor-pointer"
        data-cursor="button"
        aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
      >
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {isOpen ? (
            <X size={22} className="text-brand-purple dark:text-brand-light" />
          ) : (
            <Menu size={22} className="text-brand-purple dark:text-brand-light" />
          )}
        </motion.div>
      </motion.button>

      {/* Backdrop overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-md lg:hidden"
            onClick={toggleMenu}
          />
        )}
      </AnimatePresence>

      {/* Navigation Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed top-0 right-0 h-full w-[300px] xs:w-80 max-w-[85vw] bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border-l border-brand-light/30 dark:border-brand-purple/30 shadow-2xl z-[70] lg:hidden flex flex-col justify-between"
          >
            {/* Drawer Header */}
            <div>
              <div className="flex justify-between items-center px-5 py-4 sm:p-6 border-b border-brand-light/30 dark:border-brand-purple/20">
                <div className="flex items-center gap-2.5 text-xl font-extrabold bg-gradient-to-r from-brand-navy via-brand-purple to-brand-blue dark:from-white dark:via-brand-light dark:to-brand-purple bg-clip-text text-transparent font-inter">
                  <img
                    src="/assets/logo.png"
                    alt="Nugi Logo"
                    className="w-7 h-7 object-contain rounded-lg select-none"
                  />
                  <span>nugi<span className="text-brand-purple dark:text-brand-light">.dev</span></span>
                </div>
                <button
                  type="button"
                  onClick={toggleMenu}
                  aria-label="Close menu"
                  className="p-2 rounded-xl bg-brand-purple/10 dark:bg-brand-purple/20 text-brand-navy dark:text-brand-light hover:bg-brand-purple/20 transition-colors cursor-pointer"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Navigation Items List */}
              <div className="p-4 sm:p-6 space-y-2">
                {navItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;

                  return (
                    <motion.button
                      key={item.id}
                      variants={itemVariants}
                      initial="closed"
                      animate="open"
                      transition={{ delay: 0.05 * index }}
                      onClick={() => handleNavClick(item.id)}
                      className={`w-full flex items-center justify-between p-3.5 rounded-2xl text-left transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'bg-gradient-to-r from-brand-purple/15 to-brand-blue/15 text-brand-purple dark:text-brand-light font-bold border border-brand-purple/35 shadow-sm'
                          : 'bg-white/60 dark:bg-slate-800/40 text-brand-navy/80 dark:text-brand-dark-text-muted hover:bg-brand-purple/10 dark:hover:bg-brand-purple/20 border border-brand-light/20 dark:border-brand-purple/15'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${
                          isActive
                            ? 'bg-brand-purple/20 text-brand-purple dark:text-brand-light'
                            : 'bg-brand-navy/5 dark:bg-slate-700/50 text-brand-navy/60 dark:text-brand-dark-text-muted'
                        }`}>
                          <Icon size={18} />
                        </div>
                        <span className="text-sm font-semibold">
                          {item.label}
                        </span>
                      </div>
                      {isActive && (
                        <div className="w-1.5 h-5 rounded-full bg-gradient-to-b from-brand-purple to-brand-light" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Social Channels Footer */}
            <div className="p-5 sm:p-6 border-t border-brand-light/30 dark:border-brand-purple/20 bg-brand-light/5 dark:bg-brand-purple/5">
              <div className="text-[11px] font-bold text-brand-purple dark:text-brand-light uppercase tracking-wider mb-3 text-center">
                Connect Directly
              </div>
              <div className="flex justify-center gap-3">
                <a
                  href="https://github.com/Nugi29"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub Profile"
                  className="p-3 rounded-2xl bg-white/80 dark:bg-slate-800/80 text-brand-navy dark:text-brand-light border border-brand-light/40 dark:border-brand-purple/30 hover:bg-brand-purple hover:text-white dark:hover:bg-brand-purple transition-all shadow-sm"
                >
                  <Github size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/in/nugitha-disas"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn Profile"
                  className="p-3 rounded-2xl bg-white/80 dark:bg-slate-800/80 text-brand-navy dark:text-brand-light border border-brand-light/40 dark:border-brand-purple/30 hover:bg-brand-purple hover:text-white dark:hover:bg-brand-purple transition-all shadow-sm"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href="mailto:nugithadc@gmail.com"
                  aria-label="Send Email"
                  className="p-3 rounded-2xl bg-white/80 dark:bg-slate-800/80 text-brand-navy dark:text-brand-light border border-brand-light/40 dark:border-brand-purple/30 hover:bg-brand-purple hover:text-white dark:hover:bg-brand-purple transition-all shadow-sm"
                >
                  <Mail size={18} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileNav;
